/**
 * DUMMY/PLACEHOLDER PERSONA & STATS — replace with real data when available.
 * 'Raj' is a placeholder name, not a real credentialed person.
 */

'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  Compass, Orbit, ShieldCheck, CheckCircle2, ChevronDown, ChevronUp,
  Heart, Calculator, Sparkles, BookOpen, HelpCircle, User, Award, Clock, Star, Sun, Calendar
} from 'lucide-react';
import { ZODIAC_METADATA } from '@/lib/config/zodiac';

export function HoroscopePageContent() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const FAQS = [
    {
      q: 'How are daily, weekly, monthly, and yearly horoscopes calculated?',
      a: 'I calculate every forecast deterministically using Swiss Ephemeris (pyswisseph) C-native routines to track exact daily Moon positions and transit planetary angles relative to your Vedic Moon sign (Chandra Rashi) under Lahiri Ayanamsha.'
    },
    {
      q: 'Why does Vedic astrology focus on Moon signs rather than Sun signs?',
      a: 'In classical Parashari Vedic astrology, I focus on the Moon because it governs the mind (Manas), emotional state, and immediate perception of daily transits. Since the Moon moves through a sign every 2.25 days, its motion gives you far more responsive daily guidance than the Sun, which stays in one sign for 30 days.'
    },
    {
      q: 'What is the difference between Vedic Moon Sign and Western Sun Sign?',
      a: 'Western astrology uses the Tropical zodiac based on fixed seasonal equinoxes, whereas I use the Vedic Sidereal zodiac aligned with physical constellation coordinates (~24 degree Lahiri offset). Additionally, I place primary predictive emphasis on your Moon sign.'
    },
    {
      q: 'Are general transit horoscopes personalized to my exact birth chart?',
      a: 'General sign horoscopes provide directional transit guidance for everyone sharing a Moon sign. For personalized predictions based on your exact birth time, house lords, active Mahadasha, and Shadbala planetary strengths, I recommend using our Birth Chart Kundli Calculator and AI Astrologer.'
    },
    {
      q: 'How do Nakshatras influence daily transit predictions?',
      a: 'The 27 Nakshatras (lunar mansions) divide the zodiac into 13 deg 20 min segments. As the Moon transits specific Nakshatras each day, I analyze how it interacts with planetary ruling deities (Devatas) to color daily emotional and practical focus.'
    },
    {
      q: 'How often are the horoscope forecasts refreshed on AstroPredict?',
      a: 'Daily forecasts update fresh every 24 hours at midnight IST. Weekly forecasts refresh every Monday, monthly forecasts update on the 1st of each month, and yearly forecasts update annually.'
    },
    {
      q: 'Can I check horoscopes for other periods like Weekly or Yearly?',
      a: 'Yes! Use the period tab selector at the top of the page or the cross-link grid below to toggle seamlessly between Daily, Weekly, Monthly, and Yearly transit forecasts for any sign.'
    },
    {
      q: 'What should I do if a transit forecast indicates challenging energy?',
      a: 'Transit forecasts highlight macro trends. Remedial Vedic practices like Panchang Muhurta selection, planetary Yantra meditation, and observing Sade Sati / Dasha remedies help navigate challenging transit periods.'
    },
    {
      q: 'What is today\'s Nakshatra and how does it affect my horoscope?',
      a: 'Today\'s Nakshatra is the specific 13 deg 20 min lunar mansion currently occupied by the Moon. It sets the subtle emotional frequency and cosmic backdrop for the day across all 12 signs. Check our Panchang calculator for today\'s exact Nakshatra timing.'
    },
    {
      q: 'What is the auspicious time (Shubh Muhurat) today?',
      a: 'Shubh Muhurat represents favorable astronomical windows like Abhijit Muhurta for initiating major actions. You can view today\'s exact auspicious and inauspicious (Rahu Kaal) timing windows on our dedicated Daily Panchang page.'
    },
    {
      q: 'Can I make important decisions based on today\'s horoscope?',
      a: 'Daily horoscopes offer broad directional guidance. While helpful for general situational awareness, I always recommend combining general transit insights with your exact natal chart dasha period and daily Panchang Muhurta before making major financial or life decisions.'
    },
    {
      q: 'Is it considered unlucky to check your horoscope every day?',
      a: 'Not at all. In classical Vedic tradition, checking the daily Panchang and planetary transits is viewed as practical cosmic weather awareness, helping you align your activities with daily energy rather than acting blindly.'
    },
    {
      q: 'What daily remedies can help align with today\'s energy?',
      a: 'Classical daily remedies are simple alignment practices tied to the ruling planet of each day of the week, such as wearing planetary colors, offering gratitude, or practicing mindfulness. Refer to our "Daily Remedies: Small Actions, Big Shifts" section above for day-by-day details.'
    }
  ];

  return (
    <div className="space-y-16 mt-12 pt-12 border-t border-white/10" data-testid="horoscope-page-content">
      {/* 1. HOW THESE PREDICTIONS ARE CALCULATED */}
      <section className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="space-y-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5ab4ff] block font-mono">
              Computational Methodology
            </span>
            <h2 className="text-2xl font-['Lexend'] font-medium text-white flex items-center gap-2">
              <Compass className="w-5 h-5 text-[#5ab4ff]" />
              <span>How I Calculate These Predictions</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#dcecff]/70 bg-white/[0.04] px-3.5 py-1.5 rounded-full border border-white/10 font-mono">
            <ShieldCheck className="w-4 h-4 text-[#8dffc9]" />
            <span>Lahiri Ayanamsha + Swiss Ephemeris</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs sm:text-sm text-white leading-relaxed font-['Outfit']">
          <div className="space-y-3 bg-white/[0.03] border border-white/10 p-6 rounded-2xl">
            <h3 className="font-['Lexend'] font-semibold text-base text-[#5ab4ff] flex items-center gap-2">
              <Orbit className="w-4 h-4 text-[#5ab4ff]" />
              <span>Panchang & Planetary Transit Engine</span>
            </h3>
            <p className="text-[#dcecff]/75">
              When I construct your daily, weekly, monthly, and yearly transit forecasts, I look at the Panchang first. I pull exact planetary longitudes from C-native Swiss Ephemeris routines (pyswisseph) and evaluate the five core Panchang elements (Tithi, Nakshatra, Yoga, Karana, and Vara) to map how daily transit vectors impact your Moon sign.
            </p>
          </div>

          <div className="space-y-3 bg-white/[0.03] border border-white/10 p-6 rounded-2xl">
            <h3 className="font-['Lexend'] font-semibold text-base text-[#b9c6ff] flex items-center gap-2">
              <Compass className="w-4 h-4 text-[#b9c6ff]" />
              <span>Parashari Sidereal Moon Sign Focus</span>
            </h3>
            <p className="text-[#dcecff]/75">
              Rather than tracking Sun signs as in Western Tropical systems, I ground every prediction in your Chandra Rashi (Vedic Moon Sign). In Parashari astrology, the Moon governs your mind (Manas) and immediate emotional perception. Tracking the Moon&apos;s 2.25-day transit cycle delivers far more practical, day-to-day guidance.
            </p>
          </div>
        </div>
      </section>

      {/* 2. UNDERSTANDING TODAY'S COSMIC ENERGY / PANCHANG DEEP-DIVE */}
      <section className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6" data-testid="panchang-deep-dive-section">
        <div className="border-b border-white/10 pb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5ab4ff] block mb-1 font-mono">
            Five Panchang Pillars
          </span>
          <h2 className="text-2xl font-['Lexend'] font-medium text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#5ab4ff]" />
            <span>Understanding Today&apos;s Cosmic Energy</span>
          </h2>
          <p className="text-xs sm:text-sm text-[#dcecff]/75 mt-1 font-['Outfit']">
            Every day in Vedic astrology is governed by five astronomical components (Panch-Anga). Here is how I evaluate each element to decode the day&apos;s energy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-xs sm:text-sm font-['Outfit']">
          <div className="bg-white/[0.03] border border-white/10 p-5 rounded-2xl space-y-2">
            <h3 className="font-['Lexend'] font-semibold text-[#5ab4ff] text-sm">1. Tithi</h3>
            <p className="text-[#dcecff]/75 leading-relaxed">
              The lunar day (waxing Shukla Paksha vs waning Krishna Paksha). Dictates emotional vitality and building vs releasing focus.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-5 rounded-2xl space-y-2">
            <h3 className="font-['Lexend'] font-semibold text-white text-sm">2. Nakshatra</h3>
            <p className="text-[#dcecff]/75 leading-relaxed">
              The active 13 deg 20 min lunar mansion. Defines the underlying psychological theme and cosmic deity influence for the day.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-5 rounded-2xl space-y-2">
            <h3 className="font-['Lexend'] font-semibold text-[#b9c6ff] text-sm">3. Yoga</h3>
            <p className="text-[#dcecff]/75 leading-relaxed">
              The combined angular sum of Sun and Moon (27 Yogas total). Establishes the overarching atmospheric mood and harmony.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-5 rounded-2xl space-y-2">
            <h3 className="font-['Lexend'] font-semibold text-white text-sm">4. Karana</h3>
            <p className="text-[#dcecff]/75 leading-relaxed">
              Half of a Tithi. Determines the tactical pace of events, favoring executive execution vs quiet reflection.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-5 rounded-2xl space-y-2">
            <h3 className="font-['Lexend'] font-semibold text-[#ff9bb0] text-sm">5. Rahu Kaal</h3>
            <p className="text-[#dcecff]/75 leading-relaxed">
              A daily ~90 minute period ruled by Rahu. I suggest avoiding starting new financial contracts during this window.
            </p>
          </div>
        </div>
      </section>

      {/* 3. ABOUT OUR PREDICTIONS (Persona) */}
      <section className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-14 h-14 rounded-2xl border border-white/16 bg-gradient-to-br from-[#5ab4ff]/20 to-[#b9c6ff]/10 flex items-center justify-center shrink-0 shadow-sm" data-testid="raj-persona-avatar">
            <User className="w-7 h-7 text-[#5ab4ff]" />
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-['Lexend'] font-semibold text-white" data-testid="raj-persona-name">
                Raj Shukla
              </h2>
              <span className="text-[10px] uppercase font-bold px-2.5 py-0.5 rounded-full bg-[#5ab4ff]/15 text-[#5ab4ff] border border-[#5ab4ff]/30">
                Senior Vedic Astrology Analyst
              </span>
            </div>
            <p className="text-xs text-[#dcecff]/70 font-mono">
              Specializes in Parashari transit calculations, Panchang Muhurta analysis, and planetary dasha mapping.
            </p>
          </div>
        </div>

        <p className="text-xs sm:text-sm text-[#dcecff]/75 leading-relaxed font-['Outfit']">
          Hello! I&apos;m Raj. Over the past 12+ years of studying and writing about classical Vedic astrology, I&apos;ve authored 500+ analytical guides on planetary transits, dasha timelines, and Nakshatra energies. I oversee AstroPredict&apos;s horoscope framework to make sure every forecast combines 100% ephemeris mathematical precision with actionable, real-world advice.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center bg-white/[0.03] border border-white/10 p-5 rounded-2xl">
          <div>
            <div className="text-xl font-['Lexend'] font-bold text-[#5ab4ff]">12+ Years</div>
            <div className="text-[11px] text-[#dcecff]/60 font-['Outfit']">Transit Experience</div>
          </div>
          <div>
            <div className="text-xl font-['Lexend'] font-bold text-white">500+</div>
            <div className="text-[11px] text-[#dcecff]/60 font-['Outfit']">Vedic Articles</div>
          </div>
          <div>
            <div className="text-xl font-['Lexend'] font-bold text-[#8dffc9]">100%</div>
            <div className="text-[11px] text-[#dcecff]/60 font-['Outfit']">Ephemeris Backed</div>
          </div>
          <div>
            <div className="text-xl font-['Lexend'] font-bold text-white">27</div>
            <div className="text-[11px] text-[#dcecff]/60 font-['Outfit']">Nakshatras Mapped</div>
          </div>
        </div>
      </section>

      {/* 4. EXPANDED SUBSECTIONS */}
      <section className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-4 md:col-span-3 lg:col-span-1" data-testid="how-to-read-section">
            <div className="p-2.5 rounded-xl bg-[#5ab4ff]/10 border border-[#5ab4ff]/25 text-[#5ab4ff] w-fit">
              <BookOpen className="w-5 h-5 text-[#5ab4ff]" />
            </div>
            <h3 className="text-lg font-['Lexend'] font-semibold text-white">How to Read Your Horoscope Day to Day</h3>
            
            <div className="space-y-3 text-xs text-[#dcecff]/75 leading-relaxed font-['Outfit']">
              <p>
                <strong className="text-[#5ab4ff] block mb-1">Part A: Directional Weather, Not Fixed Script</strong>
                I always encourage readers to view transit horoscopes like a cosmic weather forecast. Rain on the radar doesn&apos;t cancel your day; it simply tells you to carry an umbrella.
              </p>
              <p>
                <strong className="text-[#5ab4ff] block mb-1">Part B: Making Informed Decisions</strong>
                If a day&apos;s energy looks confrontational per the Panchang (for example, Mars aspecting your 7th house), it is often wise to wait a day or two before bringing up sensitive topics rather than forcing an argument.
              </p>
            </div>
          </div>

          <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-4 md:col-span-3 lg:col-span-1" data-testid="expanded-nakshatras-section">
            <div className="p-2.5 rounded-xl bg-[#b9c6ff]/10 border border-[#b9c6ff]/25 text-[#b9c6ff] w-fit">
              <Star className="w-5 h-5 text-[#b9c6ff]" />
            </div>
            <h3 className="text-lg font-['Lexend'] font-semibold text-white">Why Nakshatras Matter (5 Lunar Archetypes)</h3>
            <div className="space-y-2 text-xs text-[#dcecff]/75 leading-relaxed font-['Outfit']">
              <p><strong className="text-white">Rohini:</strong> Creative growth, material abundance, and artistic focus.</p>
              <p><strong className="text-white">Pushya:</strong> Care, nourishment, and favorable spiritual beginnings.</p>
              <p><strong className="text-white">Bharani:</strong> Intense transformative drive, boundaries, and discipline.</p>
              <p><strong className="text-white">Ashlesha:</strong> Deep research, strategic investigation, and keen focus.</p>
              <p><strong className="text-white">Magha:</strong> Ancestral tradition, honor, and decisive leadership tasks.</p>
            </div>
          </div>

          <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-4 md:col-span-3 lg:col-span-1">
            <div className="p-2.5 rounded-xl bg-[#8dffc9]/10 border border-[#8dffc9]/25 text-[#8dffc9] w-fit">
              <Clock className="w-5 h-5 text-[#8dffc9]" />
            </div>
            <h3 className="text-lg font-['Lexend'] font-semibold text-white">A Note on Timing</h3>
            <p className="text-xs text-[#dcecff]/75 leading-relaxed font-['Outfit']">
              Timing is everything in Vedic astrology. Even on a positive transit day, I suggest checking daily Rahu Kaal windows to avoid scheduling major contracts during unsupportive hours, preferring Abhijit Muhurta for important launches.
            </p>
          </div>
        </div>
      </section>

      {/* 5. DAILY REMEDIES */}
      <section className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-6" data-testid="daily-remedies-section">
        <div className="border-b border-white/10 pb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5ab4ff] block mb-1 font-mono">
            Planetary Alignment Practices
          </span>
          <h2 className="text-2xl font-['Lexend'] font-medium text-white flex items-center gap-2">
            <Sun className="w-5 h-5 text-[#5ab4ff]" />
            <span>Daily Remedies: Small Actions, Big Shifts</span>
          </h2>
          <p className="text-xs sm:text-sm text-[#dcecff]/75 mt-1 font-['Outfit']">
            Here are simple, optional classical alignment practices for each day of the week based on its ruling planet.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 text-xs font-['Outfit']">
          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#ffd36b] text-sm">Sunday (Sun)</div>
            <p className="text-[#dcecff]/75">Wear orange/red, practice leadership, honor mentors.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-white text-sm">Monday (Moon)</div>
            <p className="text-[#dcecff]/75">Wear white, offer milk/water, practice meditation.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#ff9bb0] text-sm">Tuesday (Mars)</div>
            <p className="text-[#dcecff]/75">Wear red, recite Hanuman Chalisa, avoid hasty arguments.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#8dffc9] text-sm">Wednesday (Mercury)</div>
            <p className="text-[#dcecff]/75">Wear green, favor study, business accounting, and learning.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#ffd36b] text-sm">Thursday (Jupiter)</div>
            <p className="text-[#dcecff]/75">Wear yellow, honor teachers, engage in spiritual study.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#b9c6ff] text-sm">Friday (Venus)</div>
            <p className="text-[#dcecff]/75">Wear white/pink, nurture relationships, creative arts.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-4 rounded-2xl space-y-1">
            <div className="font-['Lexend'] font-semibold text-[#5ab4ff] text-sm">Saturday (Saturn)</div>
            <p className="text-[#dcecff]/75">Wear blue/black, practice discipline, service to others.</p>
          </div>
        </div>
      </section>

      {/* 6. FAQ ACCORDION */}
      <section className="space-y-6">
        <div className="border-b border-white/10 pb-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5ab4ff] block mb-1 font-mono">
            Knowledge & Clarifications
          </span>
          <h2 className="text-2xl font-['Lexend'] font-medium text-white flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#5ab4ff]" />
            <span>Frequently Asked Questions</span>
          </h2>
        </div>

        <div className="space-y-3">
          {FAQS.map((faq, idx) => {
            const isOpen = activeFaq === idx;
            return (
              <div
                key={idx}
                className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-2xl overflow-hidden backdrop-blur-xl transition-colors"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full p-5 text-left font-['Lexend'] font-semibold text-sm text-white flex items-center justify-between gap-4 hover:text-[#5ab4ff] transition-colors cursor-pointer"
                >
                  <span>{faq.q}</span>
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-[#5ab4ff] shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#dcecff]/50 shrink-0" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-xs sm:text-sm text-[#dcecff]/80 leading-relaxed border-t border-white/10 pt-4 bg-white/[0.02] font-['Outfit']">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. CROSS-LINK SECTIONS */}
      <section className="space-y-8 bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl">
        <div className="border-b border-white/10 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5ab4ff] block mb-1 font-mono">
              Explore More AstroPredict Features
            </span>
            <h2 className="text-2xl font-['Lexend'] font-medium text-white">
              Related Vedic Calculators & Compatibility Tools
            </h2>
          </div>
          <Link
            href="/all-calculators"
            className="px-5 py-2.5 rounded-full bg-white hover:bg-white/90 text-[#050a24] font-semibold text-xs shrink-0 transition-all shadow-[0_2px_12px_rgba(255,255,255,0.25)]"
            data-testid="all-calculators-crosslink"
          >
            View All 26 Calculators →
          </Link>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Compatibility Cross-link */}
          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl space-y-3">
            <div className="flex items-center gap-2 text-[#ff9bb0] font-semibold text-sm">
              <Heart className="w-4 h-4" />
              <span>Zodiac & Kundli Compatibility</span>
            </div>
            <p className="text-xs text-[#dcecff]/75 leading-relaxed font-['Outfit']">
              Explore pair compatibility across Western Sun signs and serious 36-Point Vedic Ashtakoot Guna Milan for marital matching.
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Link
                href="/compatibility"
                className="text-xs font-semibold text-[#ff9bb0] hover:underline"
                data-testid="compatibility-crosslink"
              >
                Zodiac Compatibility →
              </Link>
              <span className="text-[#dcecff]/40">•</span>
              <Link
                href="/kundli-matching"
                className="text-xs font-semibold text-[#5ab4ff] hover:underline"
                data-testid="kundli-matching-crosslink"
              >
                Kundli Matching (36 Guna) →
              </Link>
            </div>
          </div>

          {/* Core Calculators Cross-link */}
          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl space-y-3">
            <div className="flex items-center gap-2 text-[#5ab4ff] font-semibold text-sm">
              <Calculator className="w-4 h-4 text-[#5ab4ff]" />
              <span>Full Birth Chart & Dashas</span>
            </div>
            <p className="text-xs text-[#dcecff]/75 leading-relaxed font-['Outfit']">
              Generate your complete Kundli with planetary longitudes, 120-year Vimshottari Dasha cycles, and Shadbala strength breakdown.
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Link
                href="/birth-chart-calculator"
                className="text-xs font-semibold text-[#5ab4ff] hover:underline"
                data-testid="birth-chart-crosslink"
              >
                Birth Chart Calculator →
              </Link>
              <span className="text-[#dcecff]/40">•</span>
              <Link
                href="/dasha-calculator"
                className="text-xs font-semibold text-[#b9c6ff] hover:underline"
                data-testid="dasha-crosslink"
              >
                Vimshottari Dasha →
              </Link>
            </div>
          </div>
        </div>

        {/* 12 Signs x 4 Periods Deep-Link Cross-Grid */}
        <div className="pt-4 border-t border-white/10 space-y-4">
          <h3 className="text-sm font-['Lexend'] font-semibold text-white">
            Browse Forecasts by Sign & Period
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {ZODIAC_METADATA.map((z) => (
              <div key={z.sign} className="bg-white/[0.03] border border-white/10 p-3 rounded-xl space-y-1.5 text-center">
                <div className="text-xs font-['Lexend'] font-semibold text-white">{z.name}</div>
                <div className="flex items-center justify-center gap-1.5 text-[10px] text-[#5ab4ff] font-mono">
                  <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=daily`} className="hover:underline">Daily</Link>
                  <span className="text-[#dcecff]/40">•</span>
                  <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=weekly`} className="hover:underline">Wk</Link>
                  <span className="text-[#dcecff]/40">•</span>
                  <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=monthly`} className="hover:underline">Mo</Link>
                  <span className="text-[#dcecff]/40">•</span>
                  <Link href={`/horoscope/today-horoscope?sign=${z.sign}&period=yearly`} className="hover:underline">Yr</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
